<template>
  <NuxtLink :to="to" :class="ui.link" @click.stop>
    <AwBadge
      :label="display.label"
      :leading-icon="display.icon"
      size="sm"
      variant="light"
      color="secondary"
      :class="ui.badge"
    />
  </NuxtLink>
</template>

<script setup lang="ts">
import { AwBadge } from "@asap/design";
import type { TaskSubject, TaskSubjectEntity } from "@asap/shared/schemas/task/context";
import { getFullName, hasName } from "@asap/shared/utils/people";
import { toSinglePage } from "~/composables/useNavigation";

const TASK_SUBJECT_ENTITY_ICON: Record<TaskSubjectEntity, string> = {
  talents: "i-ph-identification-card",
  companies: "i-ph-buildings",
  missions: "i-ph-target",
  contracts: "i-ph-file-text",
  contacts: "i-ph-address-book",
};

const props = withDefaults(
  defineProps<{
    subject: TaskSubject;
    isDone?: boolean;
  }>(),
  {
    isDone: false,
  }
);

const { t } = useI18n();

const getTaskSubjectLabel = (subject: TaskSubject): string => {
  switch (subject.entity) {
    case "talents": {
      if (!subject.data) {
        return t("tasks.subject.talents");
      }
      if (hasName(subject.data)) {
        return getFullName(subject.data);
      }
      return t("tasks.subject.talents");
    }

    case "contacts": {
      if (!subject.data) {
        return t("tasks.subject.contacts");
      }
      if (hasName(subject.data)) {
        return getFullName(subject.data);
      }
      return t("tasks.subject.contacts");
    }

    case "companies":
      return subject.data?.name ?? t("tasks.subject.companies");

    case "missions":
      return subject.data?.job_name ?? t("tasks.subject.missions");

    case "contracts": {
      if (!subject.data) {
        return t("tasks.subject.contracts");
      }
      if (subject.data.is_amendment) {
        const talent = {
          first_name: subject.data.talent_first_name,
          last_name: subject.data.talent_last_name,
        };
        if (hasName(talent)) {
          return t("tasks.subject.amendmentWithName", { name: getFullName(talent) });
        }
        return t("tasks.subject.amendment");
      }
      return t("tasks.subject.contractWithKey", { key: subject.data.contract_key });
    }
  }
};

const display = computed(() => ({
  label: getTaskSubjectLabel(props.subject),
  icon: TASK_SUBJECT_ENTITY_ICON[props.subject.entity],
  path: `/${props.subject.entity}/${props.subject.entity_id}`,
}));
const to = computed(() => toSinglePage(display.value.path));

const ui = computed(() => ({
  link: "shrink-0 max-w-40",
  badge: ["max-w-full", props.isDone && "text-gray!"],
}));
</script>
